import { fieldCaption, formatDecimalValue, tableLines, type OrderField } from './formModel.ts'

export function tableDefaultProblem(table: OrderField): string | null {
  const columns = table.columns ?? []
  for (const column of columns) {
    const mode = column.defaultMode ?? 'never'
    if (!['never', 'always', 'whenFilled'].includes(mode)) return 'Choose when to fill the default value.'
    if (mode === 'never') continue
    const value = column.defaultValue?.trim() ?? ''
    if (!value) return `Enter a default value for ${fieldCaption(column)}.`
    if (value.length > 10000) return 'Default values must be at most 10000 characters.'
    if (column.type === 'decimal' && !/^[+-]?(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(value)) return 'Enter a default with up to 2 decimal places.'
    if (column.type === 'number' && (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value) || !Number.isFinite(Number(value)))) return 'Enter a numeric default value.'
    if (column.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid default email address.'
    if (column.type === 'date' && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) return 'Enter a valid default date.'
    if (column.type === 'select' && !column.options.includes(value)) return 'Choose a default from the column options.'
    const seen = new Set([column.id])
    let current = column
    while (current.defaultMode === 'whenFilled') {
      const source = columns.find(item => item.id === current.defaultWhenColumnId)
      if (!source || source.id === current.id) return 'Choose another existing column for the default condition.'
      if (seen.has(source.id)) return 'Conditional defaults cannot depend on each other in a loop.'
      seen.add(source.id)
      current = source
    }
  }
  return null
}

// Track only values supplied in this editing session. Loaded answers and user edits are never cleared.
export function applyTableDefaults(table: OrderField, read: (name: string) => string, write: (name: string, value: string) => void,
  automatic: Map<string, string>, changedName?: string) {
  if (changedName) automatic.delete(changedName)
  const columns = table.columns ?? []
  for (let pass = 0; pass < columns.length; pass++) {
    let changed = false
    for (const line of tableLines(table)) for (const column of columns) {
      const key = `${table.id}.${line.id}.${column.id}`
      if (key === changedName) continue
      const mode = column.defaultMode ?? 'never'
      const source = columns.find(item => item.id === column.defaultWhenColumnId && item.id !== column.id)
      const active = mode === 'always' || (mode === 'whenFilled' && source && read(`${table.id}.${line.id}.${source.id}`).trim() !== '')
      const value = column.type === 'decimal' ? formatDecimalValue(column.defaultValue ?? '') : column.defaultValue ?? ''
      const existing = read(key)
      if (!active && automatic.has(key)) {
        if (existing === automatic.get(key)) { write(key, ''); changed = true }
        automatic.delete(key)
      } else if (active && value.trim() && !existing.trim()) {
        write(key, value); automatic.set(key, value); changed = true
      }
    }
    if (!changed) break
  }
}
